import React, { useState, useEffect, useCallback } from "react";
import { Navigation } from "../components/website/navigation";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import axios from "axios";
import { API_ENDPOINTS } from "../config/apiConfig";
import { useNavigate } from "react-router-dom";
import { Footer } from "../components/website/footer";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

// Zuzhao's API Key
// const stripePromise = loadStripe("pk_test_51RBGXyRvSZGBetSdltTBNF3ES2Mvhip7x8gQ7snK6xUSHzs1vj6njHx2dAmD4srCpcp40z9f9W2ZaDrwgAntiLcl00enUNq5Ia"); 

// Jonathan's API Key
const stripePromise = loadStripe("pk_test_51RAGlNIoPZSqfl8FfQ3PpoLAeBEiCQlbWuiCuTOARQBSKXhxPsZhI4eA9feEDIyJrz0YSeuBXS5auzloF4gZ4efo006T6QmZ27");

function Subscription() {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Animation hooks
  const [titleRef, isTitleVisible] = useScrollAnimation({ threshold: 0.5, triggerOnce: true });
  const [plansRef, arePlansVisible] = useScrollAnimation({ threshold: 0.2, triggerOnce: true });

  useEffect(() => {
    // This would typically come from your API
    const availablePlans = [
      {
        id: "basic",
        name: "Basic Plan",
        price: "Free",
        stripe_price_id: "price_1RCeX0IoPZSqfl8FtUb9rPow", // Add your actual Stripe price ID here
        features: [
          "10 fleet planning sessions per month",
          "Basic route optimization",
          "Weather alerts",
          "Email support",
          "Single user license"
        ]
      },
      {
        id: "premium",
        name: "Premium Plan",
        price: 49.99,
        stripe_price_id: "price_1RC49BIoPZSqfl8FZmKxkyr2", // Add your actual Stripe price ID here
        features: [
          "Unlimited fleet planning sessions",
          "Advanced route optimization",
          "Real-time weather integration",
          "Fuel consumption analytics",
          "24/7 priority support",
          "Team collaboration tools"
        ]
      },
      {
        id: "enterprise",
        name: "Enterprise Plan",
        price: 99.99,
        stripe_price_id: "price_1RCeXlIoPZSqfl8FqpsHmxmL", // Add your actual Stripe price ID here
        features: [
          "All Premium features",
          "Custom API integration",
          "Dedicated account manager",
          "Custom reporting",
          "White-label options",
          "Unlimited team members"
        ]
      }
    ];

    setPlans(availablePlans);
    setSelectedPlan(null); // Don't select a default plan
    setLoading(false);
  }, []);

  const handleSubscribe = () => {
    if (!selectedPlan) return;
    setShowCheckout(true);
  };

  const handleBackToPlans = () => {
    setShowCheckout(false);
    setError(null);
  };

  // COMMENT THIS OUT WHEN THE SUBSCRIPTION IS READY TO BE LIVE
  const handleSelectedPlan = async (plan) => {
    setSelectedPlan(plan);
    if (plan.id === "basic") {
      console.log(localStorage.getItem('authToken'));

      try {
        // Correct way to pass headers to axios
        const response = await axios.post(
          API_ENDPOINTS.freeSubscribe,
          {}, // Empty object for request body (or add data if needed)
          {
            headers: {
              'Authorization': `Token ${localStorage.getItem('authToken')}`,
              'Content-Type': 'application/json'
            }
          }
        );

        console.log(response);
        navigate('/profile');
      } catch (error) {
        console.log("Error:", error);
        setError("Please login to continue");
        if (error.response) {
          console.log("Response data:", error.response.data);
          console.log("Response status:", error.response.status);
        }
      }
    } else if (plan.id === "premium") {
      navigate('/construction');
    } else if (plan.id === "enterprise") {
      navigate('/construction');
    }
  };

  const fetchClientSecret = useCallback(async () => {
    try {
      console.log("Sending request to:", API_ENDPOINTS.subscribe);

      const response = await axios.post(API_ENDPOINTS.subscribe, {
        price_id: selectedPlan.stripe_price_id,
        subscription_type: selectedPlan.id
      }, {
        headers: {
          'Authorization': `Token ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      console.log("Received response:", response.data);

      // Check for client_secret (snake_case) or clientSecret (camelCase)
      const clientSecret = response.data.clientSecret || response.data.client_secret;

      if (!clientSecret) {
        console.error("No client secret in response:", response.data);
        throw new Error("Server response missing client secret");
      }

      return clientSecret;
    } catch (err) {
      console.error("Error details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });

      let errorMessage = "Failed to create checkout session";
      if (err.response) {
        errorMessage = err.response.data?.error ||
          `Server error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage = "No response from server. Please check your connection.";
      }

      setError(errorMessage);
      setShowCheckout(false);
      throw new Error(errorMessage);
    }
  }, [selectedPlan]);

  const options = { fetchClientSecret };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-gray-600 font-nunito">Loading subscription plans...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Navigation />
      
      {/* Error Overlay */}
      {error && (
        <div className="fixed top-24 right-5 z-50 bg-red-100 border border-red-300 text-red-800 px-5 py-4 rounded-lg shadow-lg flex items-center max-w-md animate-fade-in">
          <div className="flex justify-between items-center w-full">
            <span className="flex-grow mr-4 font-nunito">{error}</span>
            <button
              onClick={() => setError('')}
              className="bg-transparent border-none text-red-800 text-xl font-bold cursor-pointer px-1 leading-none hover:text-red-900"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-5 py-10">
        <h1 
          ref={titleRef}
          className={`text-center text-3xl font-bold text-gray-800 mb-10 font-nunito transition-all duration-1000 ease-out transform ${
            isTitleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          Choose Your Subscription Plan
        </h1>

        {!showCheckout ? (
          // Plan selection view
          <div 
            ref={plansRef}
            className={`w-full transition-all duration-1000 ease-out transform ${
              arePlansVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="flex flex-wrap justify-center gap-8 mb-10">
              {plans.map(plan => (
                <div
                  key={plan.id}
                  className={`flex-1 min-w-[280px] max-w-[350px] bg-white rounded-lg shadow-lg p-8 transition-all duration-300 cursor-pointer relative overflow-hidden flex flex-col justify-between hover:-translate-y-1 hover:shadow-xl ${
                    selectedPlan?.id === plan.id ? 'border-2 border-blue-600 -translate-y-1 shadow-xl' : ''
                  }`}
                  onClick={() => setSelectedPlan(plan)}
                >
                  <div className="text-center mb-5 pb-5 border-b border-gray-100">
                    <h2 className="text-xl font-semibold text-gray-800 mb-2 font-nunito">
                      {plan.name}
                    </h2>
                    <div className="text-3xl font-bold text-blue-600 font-nunito">
                      {plan.price === "Free" ? "Free" : `$${plan.price}`}
                      {plan.price !== "Free" && (
                        <span className="text-base font-normal text-gray-500">/month</span>
                      )}
                    </div>
                  </div>

                  <div className="mb-8 flex-grow">
                    <ul className="pl-5 space-y-2">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="text-gray-600 font-nunito">
                          ✓ {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    className={`w-full py-3 rounded-lg text-base font-semibold cursor-pointer transition-all duration-200 mt-auto font-nunito ${
                      selectedPlan?.id === plan.id 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPlan(plan);
                      handleSelectedPlan(plan);
                    }}
                  >
                    Select Plan
                  </button>
                </div>
              ))}
              
              {/* Custom Plan Card */}
              <div className="flex-1 min-w-[280px] max-w-[350px] bg-white rounded-lg shadow-lg p-8 transition-all duration-300 relative overflow-hidden flex flex-col justify-between hover:-translate-y-1 hover:shadow-xl">
                <div className="flex flex-col justify-center leading-relaxed">
                  <h2 className="text-xl font-semibold text-center text-gray-800 mb-4 font-nunito">
                    Looking for a tailored solution?
                  </h2>
                  <p className="text-gray-600 font-nunito">
                    If you don't see a plan that perfectly fits your needs, please don't hesitate to contact us.
                    Send us an email at{' '}
                    <span className="text-blue-600 font-bold">info@amptrans.net</span>{' '}
                    and we can provide consulting and create a custom plan that matches your specific requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Checkout view
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden animate-fade-in">
            <div className="px-8 py-5 bg-gray-50 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800 text-center mb-2 font-nunito">
                Complete Your Subscription
              </h2>
              <div className="flex justify-between items-center py-2">
                <span className="font-semibold text-gray-800 font-nunito">{selectedPlan.name}</span>
                <span className="text-blue-600 font-bold font-nunito">${selectedPlan.price}/month</span>
              </div>
            </div>

            {error && (
              <div className="mx-5 my-5 p-4 bg-red-50 text-red-600 rounded-lg text-sm text-center font-nunito">
                {error}
              </div>
            )}

            <div className="min-h-[500px] p-5">
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={options}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>

            <button
              onClick={handleBackToPlans}
              className="block w-[calc(100%-40px)] mx-5 mb-5 py-3 bg-transparent text-blue-600 border border-blue-600 rounded-lg text-base font-semibold cursor-pointer transition-all duration-200 hover:bg-blue-50 font-nunito"
            >
              ← Back to Plans
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default Subscription;